import { Controller } from "@hotwired/stimulus"

// Drives the manual-pairing modal filters: entity type, free-text search and
// the cross-currency toggle. All three just rewrite the results turbo-frame's
// src, so the list reloads without touching the modal shell (and without losing
// the open modal). The toggle is a chip (aria-pressed) at the top of the results
// frame: each reload re-renders it from the request's `all_currencies`, so its
// state and the count it shows always belong to the list beneath it.
export default class extends Controller {
  static targets = ["type", "search", "allCurrencies"]
  // `frame` is the id of the results frame this modal owns. It is a value rather
  // than a constant because two pickers can share a page — the transaction list's
  // offset picker and the sidebar's document picker — and each must reload its own.
  static values = {
    url: String,
    frame: { type: String, default: "manual_pairing_results" },
    debounce: { type: Number, default: 250 }
  }

  // A single character matches nearly everything and makes the endpoint do a
  // full-table ILIKE for no user benefit — wait for a real term. An empty box
  // is meaningful though: it means "show the default list".
  static minSearchLength = 2

  disconnect() {
    this.cancel()
  }

  reload() {
    this.cancel()
    this.timeout = setTimeout(() => this.submit(), this.debounceValue)
  }

  submit() {
    const frame = document.getElementById(this.frameValue)
    if (!frame) return

    const term = this.hasSearchTarget ? this.searchTarget.value.trim() : ""
    if (term.length > 0 && term.length < this.constructor.minSearchLength) return

    const url = new URL(this.urlValue, window.location.origin)
    if (this.hasTypeTarget) url.searchParams.set("type", this.typeTarget.value)
    if (term.length > 0) url.searchParams.set("q", term)
    // Only ever sent when ON: unchecking has to produce the URL the list had
    // before the toggle existed, not `all_currencies=0` (spec D7 — with the
    // box off the candidate list is byte-identical to today's).
    if (this.allCurrenciesOn()) {
      url.searchParams.set("all_currencies", "1")
    } else {
      url.searchParams.delete("all_currencies")
    }

    frame.src = url.toString()
  }

  toggleAllCurrencies(event) {
    this.#applyAllCurrencies(event, !this.allCurrenciesOn())
  }

  showAllCurrencies(event) {
    this.#applyAllCurrencies(event, true)
  }

  #applyAllCurrencies(event, on) {
    if (event) event.preventDefault()
    if (!this.hasAllCurrenciesTarget) return

    this.setAllCurrencies(on)
    this.submit()
  }

  allCurrenciesOn() {
    return this.hasAllCurrenciesTarget && this.allCurrenciesTarget.getAttribute("aria-pressed") === "true"
  }

  setAllCurrencies(on) {
    this.allCurrenciesTarget.setAttribute("aria-pressed", String(on))
  }

  cancel() {
    if (this.timeout) clearTimeout(this.timeout)
    this.timeout = null
  }
}
